import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  AccountBalance as PlanIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  LocalOffer as DiscountIcon,
} from "@mui/icons-material";
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from "../../utils/menuFilter";
import ConfirmModal from "../../components/ConfirmModal";

const PaymentPlan = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [planData, setPlanData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  const [openModal, setOpenModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    planName: "",
    status: "Active",
  });

  // Modal states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPlanForDelete, setSelectedPlanForDelete] = useState(null);

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const fetchPaymentPlans = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=paymentplan`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Payment Plans: ${response.statusText}`);
      }
      const data = await response.json();
      const plans = Array.isArray(data.data)
        ? data.data.map((item) => ({
            id: item._id,
            planName: item.details?.planName || item.planName || "N/A",
            status: item.status ? "Active" : "Inactive",
          }))
        : [];
      setPlanData(plans);
    } catch (err) {
      setError("Failed to fetch payment plans. Please try again later.");
      console.error("Error fetching payment plans:", err);
      setPlanData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentPlans();
  }, []);

  const getFilteredPlans = () => {
    if (!Array.isArray(planData)) {
      console.warn("planData is not an array:", planData);
      return [];
    }
    return planData.filter((plan) => {
      const matchesSearch = searchQuery === "" || 
        String(plan.planName || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || plan.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
      case "Inactive":
        return { backgroundColor: "#ffebee", color: "#d32f2f" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newPlan.planName.trim()) newErrors.planName = "Plan name is required";
    return newErrors;
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setNewPlan({
      planName: plan.planName || "",
      status: plan.status || "Active",
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (plan) => {
    setSelectedPlanForDelete(plan);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPlanForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedPlanForDelete.id}`, {
        method: "PUT",
      });
      if (!response.ok) {
        throw new Error(`Failed to delete payment plan: ${response.statusText}`);
      }
      await fetchPaymentPlans(true);
      setAlertConfig({ title: 'Success', message: 'Payment plan deleted successfully!' });
      setAlertOpen(true);
    } catch (error) {
      setAlertConfig({ title: 'Error', message: error.message || "Failed to delete payment plan. Please try again." });
      setAlertOpen(true);
      console.error("Error deleting payment plan:", error);
    } finally {
      setIsLoading(false);
      setSelectedPlanForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    const count = getFilteredPlans().length;
    setAlertConfig({
      title: 'Search Results',
      message: count > 0 ? `Found ${count} payment plans matching your criteria.` : 'No payment plans found matching your criteria.'
    });
    setAlertOpen(true);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPage(0);
    setErrors({});
    fetchPaymentPlans(true);
  };

  const handleOpenModal = () => {
    setEditingPlan(null);
    setNewPlan({
      planName: "",
      status: "Active",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPlan(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPlan({
      ...newPlan,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddPlan = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoadingModal(true);
    try {
      const payload = {
        type: "paymentplan",
        details: {
          planName: newPlan.planName,
        },
        status: newPlan.status === "Active",
      };

      const url = editingPlan
        ? `${baseURL}/api/globalMasterDetails/${editingPlan.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingPlan ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingPlan ? "update" : "add"} payment plan: ${response.statusText}`
        );
      }

      await fetchPaymentPlans(true);
      handleCloseModal();
      setAlertConfig({
        title: 'Success',
        message: editingPlan ? "Payment plan updated successfully!" : "Payment plan added successfully!"
      });
      setAlertOpen(true);
    } catch (error) {
      setErrors({ general: error.message || "An error occurred. Please try again." });
      console.error("Error saving payment plan:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const filteredPlans = getFilteredPlans();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
       <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PlanIcon sx={{ mr: 1, color: "primary.main", fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Payment Plan Management
          </Typography>
        </Box>
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchPaymentPlans()}
              disabled={isLoading}
              sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Refresh
            </Button>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenModal}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
              >
                Add Payment Plan
              </Button>
            )}
          </Box>
      </Box>

      {/* Modal Alerts & Confirms */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the payment plan <strong>{selectedPlanForDelete?.planName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Payment Plans..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, height: '40px' }}
              >
                Search
              </Button>
              {/* <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, height: '40px' }}
              >
                Reset
              </Button> */}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table Section */}
      {!isLoading && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Plan Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPlans.length > 0 ? (
                  filteredPlans
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((plan, index) => (
                      <TableRow key={plan.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{plan.planName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={plan.status}
                            size="small"
                            style={getStatusStyle(plan.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canWrite && (
                              <Tooltip title="Edit Plan">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(plan)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                             {canWrite && (
                              <Tooltip title="Delete Plan">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(plan)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No payment plans found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPlans.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Add/Edit Payment Plan Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editingPlan ? "Edit Payment Plan" : "Add New Payment Plan"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name *"
                name="planName"
                value={newPlan.planName}
                onChange={handleInputChange}
                error={!!errors.planName}
                helperText={errors.planName}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newPlan.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoadingModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPlan}
            disabled={isLoadingModal}
            startIcon={isLoadingModal ? <CircularProgress size={20} /> : null}
          >
            {isLoadingModal ? "Saving..." : editingPlan ? "Update Plan" : "Add Plan"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentPlan;